import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../components/AuthContext';
import apiService from '../services/apiService';
import Modal from '../components/Modal';
import { readStoredMqttEnv } from '../utils/mqttEnv';

const statusBadge = (status) => {
    if (status === 'up_to_date') {
        return (
            <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Up to date
            </span>
        );
    }
    if (status === 'outdated') {
        return (
            <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                Outdated
            </span>
        );
    }
    return (
        <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
            Unknown
        </span>
    );
};

const FleetStatus = () => {
    const { isAdmin } = useAuth();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [skus, setSkus] = useState([]);
    const [topStations, setTopStations] = useState({});
    const [expandedSku, setExpandedSku] = useState(null);
    const [otaBusy, setOtaBusy] = useState({});
    const [otaMsg, setOtaMsg] = useState(null);

    // See more modal
    const [listModal, setListModal] = useState({ open: false, sku: null, firmwareId: null, latestVersion: null });
    const [listPage, setListPage] = useState(1);
    const [listSearch, setListSearch] = useState('');
    const [listData, setListData] = useState([]);
    const [listTotal, setListTotal] = useState(0);
    const [listLoading, setListLoading] = useState(false);
    const LIST_PAGE_SIZE = 20;

    // Station detail modal
    const [detailModal, setDetailModal] = useState({
        open: false,
        stationId: null,
        sku: null,
        firmwareId: null,
    });
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    const loadStatus = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const res = await apiService.getFleetStatus(10);
            if (res.success && res.data) {
                setSkus(res.data.skus || []);
                setTopStations(res.data.top_stations || {});
            } else {
                setError(res.error || 'Failed to load fleet status');
            }
        } catch (e) {
            setError(e.response?.data?.error || e.message || 'Failed to load fleet status');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadStatus();
    }, [loadStatus]);

    const loadListPage = useCallback(async (sku, page, search) => {
        if (!sku) return;
        setListLoading(true);
        try {
            const res = await apiService.getFleetStations({
                sku,
                page,
                pageSize: LIST_PAGE_SIZE,
                search: search || undefined,
            });
            if (res.success) {
                setListData(res.data || []);
                setListTotal(res.total || 0);
                setListPage(res.page || page);
            }
        } catch (e) {
            console.error('Fleet stations list error:', e);
            setListData([]);
            setListTotal(0);
        } finally {
            setListLoading(false);
        }
    }, []);

    const openSeeMore = (skuSummary) => {
        setListModal({
            open: true,
            sku: skuSummary.sku,
            firmwareId: skuSummary.firmware_id,
            latestVersion: skuSummary.latest_version,
        });
        setListSearch('');
        setListPage(1);
        loadListPage(skuSummary.sku, 1, '');
    };

    const openStationDetail = async (stationId, sku, firmwareId) => {
        setDetailModal({ open: true, stationId, sku, firmwareId });
        setDetail(null);
        setDetailLoading(true);
        try {
            const res = await apiService.getFleetStationDetail(stationId, sku || '');
            if (res.success) setDetail(res.data);
        } catch (e) {
            console.error('Station detail error:', e);
        } finally {
            setDetailLoading(false);
        }
    };

    const setBusy = (key, val) => setOtaBusy((prev) => ({ ...prev, [key]: val }));

    const publishStationWide = async (firmwareId, stationId, sku) => {
        if (!isAdmin() || !firmwareId || !stationId) return;
        const key = `st-${stationId}-${sku}`;
        setBusy(key, true);
        setOtaMsg(null);
        try {
            const res = await apiService.publishAdvanced(firmwareId, {
                station_ids: [stationId],
                sku_filter: sku || undefined,
                env: readStoredMqttEnv(),
            });
            if (res.success) {
                const d = res.data || {};
                setOtaMsg({
                    type: 'success',
                    message: `OTA queued for ${stationId}. Job ${d.job_id || '—'}, batch ${d.ota_batch_id || '—'}.`,
                });
            } else {
                setOtaMsg({ type: 'error', message: res.error || 'Publish failed' });
            }
        } catch (e) {
            setOtaMsg({
                type: 'error',
                message: e.response?.data?.error || e.message || 'Publish failed',
            });
        } finally {
            setBusy(key, false);
        }
    };

    const publishDeviceQuick = async (firmwareId, stationId, deviceId, connectorId) => {
        if (!isAdmin() || !firmwareId || !deviceId) return;
        const key = `dev-${deviceId}`;
        setBusy(key, true);
        setOtaMsg(null);
        try {
            // Real stations: hub owns OTA via station-wide topic (gateway self + child fan-out).
            // Lab / no station: fall back to quick connector publish.
            const hasStation = stationId && stationId !== '0' && stationId !== '__UNASSIGNED__';
            const mqttEnv = readStoredMqttEnv();
            let res;
            if (hasStation) {
                res = await apiService.publishAdvanced(firmwareId, {
                    station_ids: [stationId],
                    sku_filter: detailModal.sku || undefined,
                    env: mqttEnv,
                });
            } else {
                res = await apiService.publishFirmware(firmwareId, {
                    device_ids: [deviceId],
                    station_id: stationId || '0',
                    connector_id: connectorId || undefined,
                    env: mqttEnv,
                });
            }
            if (res.success) {
                const d = res.data || {};
                setOtaMsg({
                    type: 'success',
                    message: hasStation
                        ? `Station-wide OTA queued for ${stationId} (device ${deviceId}). Job ${d.job_id || d.ota_batch_id || '—'}.`
                        : `Quick OTA published for ${deviceId}. Batch ${d.ota_batch_id || '—'}.`,
                });
            } else {
                setOtaMsg({ type: 'error', message: res.error || 'Publish failed' });
            }
        } catch (e) {
            setOtaMsg({
                type: 'error',
                message: e.response?.data?.error || e.message || 'Publish failed',
            });
        } finally {
            setBusy(key, false);
        }
    };

    const publishAllOutdatedOnStation = async () => {
        if (!detailModal.firmwareId || !detailModal.stationId) return;
        await publishStationWide(detailModal.firmwareId, detailModal.stationId, detailModal.sku);
    };

    const listTotalPages = Math.max(1, Math.ceil(listTotal / LIST_PAGE_SIZE));

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="spinner mx-auto mb-4" />
                    <p className="text-gray-600">Loading fleet status…</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <p className="text-red-800">{error}</p>
                <button type="button" onClick={loadStatus} className="mt-2 text-red-600 underline text-sm">
                    Try again
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Fleet Status</h1>
                    <p className="text-sm text-gray-600 mt-1">
                        Compare field device firmware against the latest version uploaded per SKU.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={loadStatus}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                    Refresh
                </button>
            </div>

            {otaMsg && (
                <div
                    className={`rounded-lg p-3 text-sm ${
                        otaMsg.type === 'success'
                            ? 'bg-green-50 text-green-800 border border-green-200'
                            : 'bg-red-50 text-red-800 border border-red-200'
                    }`}
                >
                    {otaMsg.message}
                    <button type="button" className="ml-3 underline text-xs" onClick={() => setOtaMsg(null)}>
                        Dismiss
                    </button>
                </div>
            )}

            {skus.length === 0 ? (
                <div className="bg-white shadow rounded-lg p-6 text-sm text-gray-500">
                    No firmware uploaded yet. Upload firmware for a SKU to start monitoring.
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {skus.map((s) => {
                        const total = s.total_devices || 0;
                        const up = s.up_to_date || 0;
                        const out = s.outdated || 0;
                        const hasBaseline = Boolean(s.latest_version || s.has_baseline);
                        const pct = hasBaseline && total > 0 ? Math.round((up / total) * 100) : 0;
                        return (
                            <div key={s.sku} className="bg-white shadow rounded-lg p-5 border border-gray-100">
                                <div className="flex justify-between items-start gap-2">
                                    <div>
                                        <h2 className="text-lg font-semibold text-gray-900 font-mono">{s.sku}</h2>
                                        {(s.sku_name || s.platformio_env) && (
                                            <p className="text-xs text-gray-500 mt-0.5">
                                                {s.sku_name || ''}
                                                {s.platformio_env
                                                    ? `${s.sku_name ? ' · ' : ''}OTA ${s.platformio_env}`
                                                    : ''}
                                            </p>
                                        )}
                                        <p className="text-sm text-gray-500 mt-0.5">
                                            Latest{' '}
                                            <span className="font-medium text-gray-800">
                                                {s.latest_version || '—'}
                                            </span>
                                        </p>
                                    </div>
                                    {!hasBaseline ? (
                                        <span className="text-xs font-medium px-2 py-1 rounded-full bg-gray-100 text-gray-600">
                                            No firmware
                                        </span>
                                    ) : out > 0 ? (
                                        <span className="text-xs font-medium px-2 py-1 rounded-full bg-amber-100 text-amber-800">
                                            {out} outdated
                                        </span>
                                    ) : total > 0 ? (
                                        <span className="text-xs font-medium px-2 py-1 rounded-full bg-green-100 text-green-800">
                                            All current
                                        </span>
                                    ) : (
                                        <span className="text-xs font-medium px-2 py-1 rounded-full bg-gray-100 text-gray-600">
                                            No devices
                                        </span>
                                    )}
                                </div>
                                <div className="mt-4 grid grid-cols-3 gap-2 text-center text-sm">
                                    <div>
                                        <div className="text-xs text-gray-500">Total</div>
                                        <div className="text-lg font-semibold text-gray-900">{total}</div>
                                    </div>
                                    <div>
                                        <div className="text-xs text-gray-500">Up to date</div>
                                        <div className="text-lg font-semibold text-green-700">
                                            {hasBaseline ? up : '—'}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-xs text-gray-500">Outdated</div>
                                        <div className="text-lg font-semibold text-amber-700">
                                            {hasBaseline ? out : '—'}
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-3 h-2 bg-gray-100 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-green-500 rounded-full transition-all"
                                        style={{ width: `${pct}%` }}
                                    />
                                </div>
                                <p className="text-xs text-gray-400 mt-1">
                                    {hasBaseline
                                        ? `${pct}% on latest`
                                        : 'Upload firmware to compare field versions'}
                                </p>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Top critical stations per SKU — only when lag stations exist */}
            {skus.map((s) => {
                const top = topStations[s.sku];
                const stations = top?.stations || [];
                const isOpen = expandedSku === s.sku;
                if (stations.length === 0) return null;
                return (
                    <div key={`top-${s.sku}`} className="bg-white shadow rounded-lg border border-gray-100">
                        <button
                            type="button"
                            className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50"
                            onClick={() => setExpandedSku(isOpen ? null : s.sku)}
                        >
                            <div>
                                <h3 className="text-base font-semibold text-gray-900">
                                    Critical lag — <span className="font-mono">{s.sku}</span>
                                </h3>
                                <p className="text-xs text-gray-500 mt-0.5">
                                    Top stations needing update to {s.latest_version}
                                </p>
                            </div>
                            <span className="text-gray-400">{isOpen ? '▾' : '▸'}</span>
                        </button>
                        {isOpen && (
                            <div className="px-5 pb-5 border-t border-gray-100">
                                {stations.length === 0 ? (
                                    <p className="text-sm text-gray-500 py-4">No stations with outdated devices.</p>
                                ) : (
                                    <div className="overflow-x-auto mt-3">
                                        <table className="min-w-full text-sm">
                                            <thead className="text-left text-xs text-gray-500 uppercase">
                                                <tr>
                                                    <th className="py-2 pr-3">Station</th>
                                                    <th className="py-2 pr-3">State</th>
                                                    <th className="py-2 pr-3 text-right">Outdated</th>
                                                    <th className="py-2 pr-3 text-right">Total</th>
                                                    <th className="py-2 text-right">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100">
                                                {stations.map((st) => {
                                                    const busyKey = `st-${st.id}-${s.sku}`;
                                                    const unassigned = !!st.unassigned || st.id === '__UNASSIGNED__';
                                                    return (
                                                        <tr key={st.id} className="hover:bg-gray-50">
                                                            <td className="py-2.5 pr-3">
                                                                <button
                                                                    type="button"
                                                                    className="text-left text-primary-700 hover:underline font-medium"
                                                                    onClick={() =>
                                                                        openStationDetail(st.id, s.sku, s.firmware_id)
                                                                    }
                                                                >
                                                                    {st.name || st.id}
                                                                </button>
                                                                <div className="text-xs text-gray-400 font-mono">
                                                                    {unassigned ? 'no station_id' : st.id}
                                                                </div>
                                                            </td>
                                                            <td className="py-2.5 pr-3 text-gray-600">
                                                                {unassigned ? '—' : st.state || '—'}
                                                            </td>
                                                            <td className="py-2.5 pr-3 text-right font-semibold text-amber-700">
                                                                {st.outdated_count}
                                                            </td>
                                                            <td className="py-2.5 pr-3 text-right text-gray-600">
                                                                {st.total_devices}
                                                            </td>
                                                            <td className="py-2.5 text-right space-x-2">
                                                                <button
                                                                    type="button"
                                                                    className="text-xs text-primary-700 hover:underline"
                                                                    onClick={() =>
                                                                        openStationDetail(st.id, s.sku, s.firmware_id)
                                                                    }
                                                                >
                                                                    Details
                                                                </button>
                                                                {isAdmin() && !unassigned && (
                                                                    <button
                                                                        type="button"
                                                                        disabled={!!otaBusy[busyKey]}
                                                                        onClick={() =>
                                                                            publishStationWide(
                                                                                s.firmware_id,
                                                                                st.id,
                                                                                s.sku
                                                                            )
                                                                        }
                                                                        className="text-xs px-2 py-1 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                                                                    >
                                                                        {otaBusy[busyKey] ? '…' : 'Update All'}
                                                                    </button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                                <div className="mt-3 flex justify-end">
                                    <button
                                        type="button"
                                        onClick={() => openSeeMore(s)}
                                        className="text-sm text-primary-700 hover:text-primary-900 font-medium"
                                    >
                                        See more →
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}

            {/* See more — paginated stations */}
            <Modal
                open={listModal.open}
                onClose={() => setListModal({ open: false, sku: null, firmwareId: null, latestVersion: null })}
                title={`Stations needing update — ${listModal.sku || ''}`}
                maxWidth="max-w-3xl"
            >
                <div className="space-y-3">
                    <p className="text-xs text-gray-500">
                        Latest firmware: <span className="font-mono">{listModal.latestVersion}</span>
                    </p>
                    <div className="flex gap-2">
                        <input
                            type="search"
                            value={listSearch}
                            onChange={(e) => setListSearch(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') loadListPage(listModal.sku, 1, listSearch);
                            }}
                            placeholder="Search stations…"
                            className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                        <button
                            type="button"
                            onClick={() => loadListPage(listModal.sku, 1, listSearch)}
                            className="px-3 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50"
                        >
                            Search
                        </button>
                    </div>
                    {listLoading ? (
                        <p className="text-sm text-gray-500 py-6 text-center">Loading…</p>
                    ) : listData.length === 0 ? (
                        <p className="text-sm text-gray-500 py-6 text-center">No stations found.</p>
                    ) : (
                        <div className="overflow-x-auto max-h-96">
                            <table className="min-w-full text-sm">
                                <thead className="text-left text-xs text-gray-500 uppercase sticky top-0 bg-white">
                                    <tr>
                                        <th className="py-2 pr-3">Station</th>
                                        <th className="py-2 pr-3">State</th>
                                        <th className="py-2 pr-3 text-right">Outdated</th>
                                        <th className="py-2 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {listData.map((st) => {
                                        const busyKey = `st-${st.id}-${listModal.sku}`;
                                        return (
                                            <tr key={st.id} className="hover:bg-gray-50">
                                                <td className="py-2 pr-3">
                                                    <button
                                                        type="button"
                                                        className="text-primary-700 hover:underline font-medium text-left"
                                                        onClick={() =>
                                                            openStationDetail(
                                                                st.id,
                                                                listModal.sku,
                                                                listModal.firmwareId
                                                            )
                                                        }
                                                    >
                                                        {st.name || st.id}
                                                    </button>
                                                    <div className="text-xs font-mono text-gray-400">{st.id}</div>
                                                </td>
                                                <td className="py-2 pr-3 text-gray-600">{st.state || '—'}</td>
                                                <td className="py-2 pr-3 text-right text-amber-700 font-semibold">
                                                    {st.outdated_count}/{st.total_devices}
                                                </td>
                                                <td className="py-2 text-right space-x-2">
                                                    <button
                                                        type="button"
                                                        className="text-xs text-primary-700 hover:underline"
                                                        onClick={() =>
                                                            openStationDetail(
                                                                st.id,
                                                                listModal.sku,
                                                                listModal.firmwareId
                                                            )
                                                        }
                                                    >
                                                        Details
                                                    </button>
                                                    {isAdmin() && (
                                                        <button
                                                            type="button"
                                                            disabled={!!otaBusy[busyKey]}
                                                            onClick={() =>
                                                                publishStationWide(
                                                                    listModal.firmwareId,
                                                                    st.id,
                                                                    listModal.sku
                                                                )
                                                            }
                                                            className="text-xs px-2 py-1 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                                                        >
                                                            {otaBusy[busyKey] ? '…' : 'Update'}
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                    <div className="flex items-center justify-between text-xs text-gray-600 pt-2 border-t border-gray-100">
                        <span>
                            Page {listPage} of {listTotalPages} · {listTotal} stations
                        </span>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                disabled={listPage <= 1 || listLoading}
                                onClick={() => loadListPage(listModal.sku, listPage - 1, listSearch)}
                                className="px-2 py-1 border rounded disabled:opacity-40"
                            >
                                Prev
                            </button>
                            <button
                                type="button"
                                disabled={listPage >= listTotalPages || listLoading}
                                onClick={() => loadListPage(listModal.sku, listPage + 1, listSearch)}
                                className="px-2 py-1 border rounded disabled:opacity-40"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>

            {/* Station detail */}
            <Modal
                open={detailModal.open}
                onClose={() => {
                    setDetailModal({ open: false, stationId: null, sku: null, firmwareId: null });
                    setDetail(null);
                }}
                title={
                    detail?.station
                        ? `${detail.station.name || detail.station.id}`
                        : `Station ${detailModal.stationId || ''}`
                }
                maxWidth="max-w-4xl"
            >
                {detailLoading ? (
                    <p className="text-sm text-gray-500 py-8 text-center">Loading devices…</p>
                ) : !detail ? (
                    <p className="text-sm text-gray-500 py-8 text-center">Station not found.</p>
                ) : (
                    <div className="space-y-4">
                        <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                            <div className="text-gray-600">
                                <span className="font-mono text-xs">{detail.station.id}</span>
                                {detail.station.city || detail.station.state ? (
                                    <span className="ml-2">
                                        {[detail.station.city, detail.station.state].filter(Boolean).join(', ')}
                                    </span>
                                ) : null}
                                {detailModal.sku ? (
                                    <span className="ml-2 text-xs bg-slate-100 px-2 py-0.5 rounded font-mono">
                                        {detailModal.sku}
                                    </span>
                                ) : null}
                            </div>
                            <div className="flex items-center gap-3">
                                <span className="text-xs text-gray-500">
                                    {detail.summary.outdated} outdated / {detail.summary.total_devices} devices
                                </span>
                                {isAdmin() && detail.summary.outdated > 0 && detailModal.firmwareId && (
                                    <button
                                        type="button"
                                        disabled={!!otaBusy[`st-${detailModal.stationId}-${detailModal.sku}`]}
                                        onClick={publishAllOutdatedOnStation}
                                        className="text-xs px-3 py-1.5 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                                    >
                                        Update All Outdated
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="overflow-x-auto max-h-[55vh]">
                            <table className="min-w-full text-sm">
                                <thead className="text-left text-xs text-gray-500 uppercase sticky top-0 bg-white">
                                    <tr>
                                        <th className="py-2 pr-2">Device</th>
                                        <th className="py-2 pr-2">Type</th>
                                        <th className="py-2 pr-2">Connector</th>
                                        <th className="py-2 pr-2">Current</th>
                                        <th className="py-2 pr-2">Latest</th>
                                        <th className="py-2 pr-2">Status</th>
                                        <th className="py-2 text-right">OTA</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {(detail.devices || []).map((d) => {
                                        const busyKey = `dev-${d.id}`;
                                        const fwId = d.firmware_id || detailModal.firmwareId;
                                        return (
                                            <tr key={d.id} className="hover:bg-gray-50">
                                                <td className="py-2 pr-2">
                                                    <div className="font-mono text-xs text-gray-900">{d.id}</div>
                                                    {d.name && d.name !== d.id ? (
                                                        <div className="text-xs text-gray-500">{d.name}</div>
                                                    ) : null}
                                                </td>
                                                <td className="py-2 pr-2 text-xs text-gray-600">{d.device_type || '—'}</td>
                                                <td className="py-2 pr-2 font-mono text-xs text-gray-600">
                                                    {d.connector_id || '—'}
                                                </td>
                                                <td className="py-2 pr-2 font-mono text-xs">
                                                    {d.firmware_version || '—'}
                                                </td>
                                                <td className="py-2 pr-2 font-mono text-xs text-gray-500">
                                                    {d.latest_version || '—'}
                                                </td>
                                                <td className="py-2 pr-2">{statusBadge(d.status)}</td>
                                                <td className="py-2 text-right">
                                                    {isAdmin() && fwId && (
                                                        <button
                                                            type="button"
                                                            disabled={!!otaBusy[busyKey]}
                                                            onClick={() =>
                                                                publishDeviceQuick(
                                                                    fwId,
                                                                    detail.station.id,
                                                                    d.id,
                                                                    d.connector_id
                                                                )
                                                            }
                                                            className="text-xs px-2 py-1 rounded bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
                                                        >
                                                            {otaBusy[busyKey] ? '…' : 'OTA Now'}
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            {(detail.devices || []).length === 0 && (
                                <p className="text-sm text-gray-500 py-6 text-center">
                                    No devices match this SKU at this station.
                                </p>
                            )}
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default FleetStatus;
